import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Mic, Plus, Clock, TrendingUp, Play, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import VoiceModal from '../components/VoiceModal';
import TimeEntryCard from '../components/TimeEntryCard';
import TimeEntryEditModal from '../components/TimeEntryEditModal';
import EnhancedSummary from '../components/EnhancedSummary';
import ProjectDashboard from '../components/ProjectDashboard';
import ToDoSummary from '../components/ToDoSummary';
import RunningTimer from '../components/RunningTimer';
import StartTimerModal from '../components/StartTimerModal';
import HelpModal from '../components/HelpModal';
import ManualEntryModal from '../components/ManualEntryModal';
import { AnimatedLogoRolodex } from '../components/ui/animated-logo-rolodex';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Home = () => {
  const { user, subscriptionInfo, refreshUser } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [timerModalOpen, setTimerModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [manualEntryModalOpen, setManualEntryModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEntries();
    fetchProjects();
    refreshUser();

    // Listen for custom event from bottom nav
    const handleOpenVoiceModal = () => {
      setVoiceModalOpen(true);
    };

    // Listen for time entries update event (from notes linking, etc.)
    const handleTimeEntriesUpdated = () => {
      fetchEntries();
    };

    // Listen for open help modal event (from onboarding, etc.)
    const handleOpenHelpModal = () => {
      setHelpModalOpen(true);
    };

    window.addEventListener('openVoiceModal', handleOpenVoiceModal);
    window.addEventListener('timeEntriesUpdated', handleTimeEntriesUpdated);
    window.addEventListener('openHelpModal', handleOpenHelpModal);

    // Keyboard shortcuts
    const handleKeyPress = (e) => {
      // Ignore if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      switch(e.key.toLowerCase()) {
        case ' ':
          // Space - Toggle timer (start/stop)
          e.preventDefault();
          setTimerModalOpen(true);
          break;
        case 'n':
          // N - New manual entry
          e.preventDefault();
          setManualEntryModalOpen(true);
          break;
        case 's':
          // S - Go to Settings
          e.preventDefault();
          navigate('/settings');
          break;
        case 'escape':
          // Esc - Close any open modal
          setVoiceModalOpen(false);
          setManualEntryModalOpen(false);
          setTimerModalOpen(false);
          setEditModalOpen(false);
          setHelpModalOpen(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('openVoiceModal', handleOpenVoiceModal);
      window.removeEventListener('timeEntriesUpdated', handleTimeEntriesUpdated);
      window.removeEventListener('openHelpModal', handleOpenHelpModal);
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [navigate]); // fetchEntries is stable, no need to add to deps

  useEffect(() => {
    if (subscriptionInfo && !subscriptionInfo.can_access) {
      navigate('/subscribe');
    }
  }, [subscriptionInfo, navigate]);

  const fetchEntries = async () => {
    try {
      // Fetch all entries (not just today) for weekly summary
      const response = await axios.get(`${API}/time-entries`);
      setEntries(response.data);
      
      // Calculate today's total
      const today = new Date().toISOString().split('T')[0];
      const todayEntries = response.data.filter(e => {
        const entryDate = new Date(e.date).toISOString().split('T')[0];
        return entryDate === today;
      });
      const total = todayEntries.reduce((sum, e) => sum + e.duration_minutes, 0);
      setTotalMinutes(total);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const handleVoiceComplete = () => {
    setVoiceModalOpen(false);
    fetchEntries();
    toast.success('Time entry added successfully!');
  };

  const handleQuickStartTimer = async (projectId) => {
    try {
      const response = await axios.post(`${API}/timer/start`, {
        project_id: projectId
      });

      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        toast.success('Timer started!');
        // Force refresh to show timer
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to start timer:', error);
      toast.error('Failed to start timer');
    }
  };

  const handleTimerStop = () => {
    fetchEntries();
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${API}/time-entries/${entryId}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setEditModalOpen(true);
  };

  const handleEditComplete = () => {
    setEditModalOpen(false);
    setSelectedEntry(null);
    fetchEntries();
    toast.success('Entry updated successfully!');
  };

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 transition-colors">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-md border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex flex-col">
                <div className="flex items-center gap-3 mb-2">
                  {/* Replace Static Logo with Animated Logo */}
                  <AnimatedLogoRolodex size="small" />
                  {/* Logly Name */}
                  <span className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Logly
                  </span>
                </div>
                  {/* Tagline - aligned with logo and name */}
                <p 
                  className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-1 transition-colors"
                  role="text"
                  aria-label="Voice-Powered Time Tracking"
                >
                  Voice-Powered Time Tracking
                </p>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mt-2 transition-colors">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
            </div>
            <div className="flex items-center gap-3">
              {subscriptionInfo && subscriptionInfo.status === 'trial' && (
                <div className="inline-block px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-sm font-medium transition-colors" data-testid="trial-banner">
                  Trial: {subscriptionInfo.trial_days_left} days left
                </div>
              )}
              <button
                onClick={() => setHelpModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                data-testid="help-button"
              >
                <HelpCircle className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <span className="text-gray-700 dark:text-gray-300 font-medium">Help</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Summary Card */}
        <Card className="bg-gradient-to-br from-cyan-500 to-teal-500 dark:from-cyan-600 dark:to-teal-600 text-white shadow-xl mb-6 border-0 transition-colors" data-testid="summary-card">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-sm mb-1">Today's Total</p>
                <h2 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }} data-testid="total-hours">
                  {hours}h {minutes}m
                </h2>
              </div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Clock className="w-8 h-8" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-cyan-100">
              <TrendingUp className="w-4 h-4 mr-1" />
              <span className="text-sm">{entries.length} entries logged</span>
            </div>
          </div>
        </Card>

        {/* Start New Timer Section */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">Start New Timer</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* MANUAL ENTRY CARD */}
            <button 
              onClick={() => setManualEntryModalOpen(true)}
              className="group bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/30 border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-6 sm:p-8 hover:shadow-xl hover:scale-105 transition-all duration-300 min-h-[200px] flex flex-col items-center justify-center text-center"
              data-testid="manual-entry-button"
            >
              <div className="w-18 h-18 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Plus className="w-9 h-9 sm:w-10 sm:h-10 text-white" />
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 mb-2 transition-colors">
                Manual Entry
              </h4>
              <p className="text-gray-600 dark:text-gray-400 text-sm transition-colors">
                Type project details and start tracking
              </p>
            </button>

            {/* VOICE ENTRY CARD */}
            <button 
              onClick={() => setVoiceModalOpen(true)}
              className="group bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-900/30 border-2 border-pink-200 dark:border-pink-800 rounded-2xl p-6 sm:p-8 hover:shadow-xl hover:scale-105 transition-all duration-300 min-h-[200px] flex flex-col items-center justify-center text-center"
              data-testid="voice-entry-button"
            >
              <div className="w-18 h-18 sm:w-20 sm:h-20 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Mic className="w-9 h-9 sm:w-10 sm:h-10 text-white" />
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 mb-2 transition-colors">
                Voice Entry
              </h4>
              <p className="text-gray-600 dark:text-gray-400 text-sm transition-colors">
                Tap to log with your voice
              </p>
            </button>
          </div>
        </div>

        {/* Quick Start Timers - Redesigned */}
        {projects.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2 transition-colors">
              <Clock className="w-5 h-5 text-cyan-500 dark:text-cyan-400" />
              Quick Start Timer
            </h3>
            <div className={`grid gap-4 mb-6 ${
              projects.length === 1 ? 'grid-cols-1' :
              projects.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
              projects.length === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
              projects.length === 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' :
              'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}>
              {projects.map((project, index) => {
                // Define color variants - cycle through them with dark mode support
                const colorVariants = [
                  { bg: 'bg-blue-100 dark:bg-blue-900/20', border: 'border-blue-300 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
                  { bg: 'bg-green-100 dark:bg-green-900/20', border: 'border-green-300 dark:border-green-800', text: 'text-green-700 dark:text-green-300', dot: 'bg-green-500' },
                  { bg: 'bg-orange-100 dark:bg-orange-900/20', border: 'border-orange-300 dark:border-orange-800', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-500' },
                  { bg: 'bg-purple-100 dark:bg-purple-900/20', border: 'border-purple-300 dark:border-purple-800', text: 'text-purple-700 dark:text-purple-300', dot: 'bg-purple-500' },
                  { bg: 'bg-pink-100 dark:bg-pink-900/20', border: 'border-pink-300 dark:border-pink-800', text: 'text-pink-700 dark:text-pink-300', dot: 'bg-pink-500' },
                  { bg: 'bg-indigo-100 dark:bg-indigo-900/20', border: 'border-indigo-300 dark:border-indigo-800', text: 'text-indigo-700 dark:text-indigo-300', dot: 'bg-indigo-500' },
                  { bg: 'bg-teal-100 dark:bg-teal-900/20', border: 'border-teal-300 dark:border-teal-800', text: 'text-teal-700 dark:text-teal-300', dot: 'bg-teal-500' },
                  { bg: 'bg-cyan-100 dark:bg-cyan-900/20', border: 'border-cyan-300 dark:border-cyan-800', text: 'text-cyan-700 dark:text-cyan-300', dot: 'bg-cyan-500' }
                ];
                const variant = colorVariants[index % colorVariants.length];
                
                return (
                  <div
                    key={project.id}
                    className={`${variant.bg} border-2 ${variant.border} rounded-xl p-4 sm:p-5 transition-all duration-200 hover:shadow-md hover:scale-105 cursor-pointer`}
                    onClick={() => handleQuickStartTimer(project.id)}
                    data-testid={`timer-card-${project.id}`}
                  >
                    {/* Project Name with Dot */}
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-3 h-3 rounded-full ${variant.dot} flex-shrink-0`} />
                      <h4 className={`font-semibold text-base sm:text-lg ${variant.text} truncate`}>{project.name}</h4>
                    </div>
                    
                    {/* Label */}
                    <p className={`text-xs uppercase tracking-wide opacity-70 ${variant.text} mb-2 sm:mb-3`}>PROJECT</p>
                    
                    {/* Time Display */}
                    <p className={`text-2xl sm:text-3xl font-bold font-mono text-center mb-3 sm:mb-4 ${variant.text}`}>00:00:00</p>
                    
                    {/* Start Button */}
                    <button
                      className="w-full bg-white dark:bg-gray-700 bg-opacity-50 dark:bg-opacity-50 hover:bg-opacity-80 dark:hover:bg-opacity-70 rounded-lg py-2 sm:py-3 flex items-center justify-center gap-2 transition-all border border-gray-200 dark:border-gray-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuickStartTimer(project.id);
                      }}
                    >
                      <Play className={`w-4 h-4 sm:w-5 sm:h-5 ${variant.text} fill-current`} />
                      <span className={`font-medium text-sm sm:text-base ${variant.text}`}>Start Timer</span>
                    </button>
                  </div>
                );
              })}
            </div>
            
            {/* Add New Timer Button */}
            <button
              onClick={() => setTimerModalOpen(true)}
              className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-cyan-400 dark:hover:border-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-xl py-4 flex items-center justify-center gap-2 transition-all"
              data-testid="add-timer-button"
            >
              <Plus className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <span className="text-gray-600 dark:text-gray-400 font-medium transition-colors">Start timer with description</span>
            </button>
          </div>
        )}

        {/* Enhanced Project Summary Dashboard */}
        <ProjectDashboard />

        {/* To-Do Summary */}
        <ToDoSummary />

        {/* Entries List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : entries.length === 0 ? (
            <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors" data-testid="empty-state">
              <div className="p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center">
                  <Mic className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>No entries yet today</h3>
                <p className="text-gray-600 dark:text-gray-400 transition-colors">Tap the mic button to start tracking your time!</p>
              </div>
            </Card>
          ) : (
            entries.map(entry => (
              <TimeEntryCard
                key={entry.id}
                entry={entry}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            ))
          )}
        </div>
      </div>

      {/* Voice Modal */}
      <VoiceModal
        open={voiceModalOpen}
        onClose={() => setVoiceModalOpen(false)}
        onComplete={handleVoiceComplete}
      />

      {/* Edit Modal */}
      <TimeEntryEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onComplete={handleEditComplete}
        entry={selectedEntry}
        prefilledData={null}
      />

      {/* Manual Entry Modal */}
      <ManualEntryModal
        open={manualEntryModalOpen}
        onClose={() => setManualEntryModalOpen(false)}
        onSuccess={fetchEntries}
      />

      {/* Start Timer Modal */}
      <StartTimerModal
        open={timerModalOpen}
        onClose={() => setTimerModalOpen(false)}
        onStart={() => {
          setTimerModalOpen(false);
          window.location.reload();
        }}
        projects={projects}
      />

      {/* Running Timer Widget */}
      <RunningTimer onTimerStop={handleTimerStop} />

      {/* Help Modal */}
      <HelpModal open={helpModalOpen} onClose={() => setHelpModalOpen(false)} />

      {/* Bottom Navigation */}
      <BottomNav active="home" />
    </div>
  );
};

export default Home;
